// The id IS the label here, and the id is what gets stored on the shot and sent to the model.
// So a reword is a content change, not a display tweak: "Foreground obstruction between camera
// and subject" was shortened to fit the menu without truncating (James, 2026-08-27, who
// proposed this exact wording). A shot already holding the old string still renders its chip --
// the chip re-offers whatever value it is showing -- it simply is not in the menu any more.
export const DEVICE_OPTIONS = Object.freeze([
  'Low angle as status',
  'High angle as vulnerability',
  'An object intrudes from off-screen',
  'A punch-in on silence',
  'Obstruction between camera and subject',
  'Reflection carries the subject',
  'Negative space isolates the subject',
  'The camera holds after the action ends',
].map((label) => Object.freeze({ id: label, label, description: null })));

export const BLOCKING_POSITIONS = Object.freeze([
  'frame-left foreground',
  'frame-right foreground',
  'frame-left background',
  'frame-right background',
]);

export const PERFORMANCE_CUES = Object.freeze([
  '{name} hides a tremor in the hands',
  "{name}'s smile never reaches the eyes",
  '{name} holds eye contact a beat too long',
  "{name}'s gaze keeps sliding off-frame",
  '{name} stays perfectly still under pressure',
  '{name} turns away before finishing the gesture',
]);

function capitalizeFirstLetter(value) {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

export function buildBlockingOptions(names = []) {
  const subjects = (Array.isArray(names) && names.length > 0 ? names : ['Subject'])
    .slice(0, 3)
    .map((name) => {
      const normalized = typeof name === 'string' ? name.trim() : '';
      return capitalizeFirstLetter(normalized || 'Subject');
    });

  return Object.freeze(subjects.flatMap((name) => (
    BLOCKING_POSITIONS.map((position) => {
      const label = `${name} ${position}`;
      return Object.freeze({ id: label, label, description: null });
    })
  )));
}

export function buildPerformanceOptions(name = 'Subject') {
  const normalized = typeof name === 'string' ? name.trim() : '';
  const subject = capitalizeFirstLetter(normalized || 'Subject');

  return Object.freeze(PERFORMANCE_CUES.map((cue) => {
    const label = cue.replace('{name}', subject);
    return Object.freeze({ id: label, label, description: null });
  }));
}

/**
 * The options a shot-prompt slot chip should offer.
 *
 * Extracted from `StoryboardShotPromptSection` because it is a decision, not rendering, and the
 * options it hands back already live in this module. Duration is the odd one out and takes a
 * resolver: its domain belongs to the BOARD -- a share of a fixed sequence total, or a cap from
 * the per-shot video model -- and only the strip knows every frame and the selected model.
 * Everything else is a fact about this shot alone and is answered here.
 *
 * Returning null means "no opinion", which is the contract `InlineSlotChip` already has: the
 * chip then falls back to its own catalog.
 */
export function resolveShotPromptSlotOptions({
  slotPath,
  durationSlotPath,
  durationOptions = null,
  resolveDurationOptions = null,
  frameId = null,
  blockingOptions = null,
  castChipNames = [],
  castRoles = [],
  getCastPerformanceSlotIndex,
  getCastPerformanceSlotRole,
} = {}) {
  if (slotPath === durationSlotPath) {
    return resolveDurationOptions
      ? resolveDurationOptions(frameId, durationOptions)
      : durationOptions;
  }

  if (slotPath === 'direction.blocking') return blockingOptions;

  const castIndex = getCastPerformanceSlotIndex?.(slotPath) ?? null;
  if (castIndex !== null) {
    return buildPerformanceOptions(castChipNames[castIndex] || 'Subject');
  }

  // The same options, reached by the other address. A chip already in the sentence reports
  // its ROLE path, and `castChipNames` is role-first by construction
  // (`useStoryboardShotCastNames`), so the role IS the name to phrase the cues with -- no
  // role->index hop, and no way for the two addresses to offer different menus.
  //
  // ⛔ ONLY when that role is still IN the cast. An orphaned anchor -- the member was
  // unbound or recast -- otherwise opened a menu of six live-looking cues whose every pick
  // `writeStoryboardShotSlot` then refused. A control that cannot do anything must not look
  // like one; an empty list is what says the anchor has lost its character.
  const role = getCastPerformanceSlotRole?.(slotPath) ?? null;
  if (role !== null) {
    // ⛔ Membership comes from the cast's ROLES, never from `castChipNames`. That list falls
    // back to the ASSET NAME for a role-less entry, so an orphaned anchor whose role happens
    // to equal some asset's name read as bound and offered actionable cues -- while the
    // write router, which searches `entry.role`, refused every one of them. The label list
    // answers "what do we call them"; only the roles answer "is this member here".
    const roles = Array.isArray(castRoles) ? castRoles : [];
    const bound = roles.some((entry) => typeof entry === 'string' && entry.trim() === role);
    return bound ? buildPerformanceOptions(role) : [];
  }

  return null;
}

/** Same cues as shot direction, with the subject supplied by the cascade. */
export function buildTellOptions() {
  return Object.freeze(PERFORMANCE_CUES.map((cue) => {
    const label = cue.replace(/^\{name\}(?:'s)? /, '');
    return Object.freeze({ id: label, label, description: null });
  }));
}
